package com.merkost.handpass.sharedweb.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.sharedweb.theme.Accent
import com.merkost.handpass.sharedweb.theme.AccentRing
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusButton
import com.merkost.handpass.sharedweb.theme.Shadow
import com.merkost.handpass.sharedweb.theme.Surface
import com.merkost.handpass.sharedweb.theme.SurfaceMuted
import com.merkost.handpass.sharedweb.theme.TouchTarget
import com.merkost.handpass.sharedweb.theme.Transparent
import com.merkost.handpass.sharedweb.theme.WebIconSize
import com.merkost.handpass.sharedweb.theme.WebType
import com.merkost.handpass.sharedweb.theme.WebMotion
import com.merkost.handpass.sharedweb.theme.WebSize
import com.merkost.handpass.sharedweb.theme.transitionSurfaceAction
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.scale
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLButtonElement

data class WebSegment<T>(val key: T, val label: String, val icon: WebIcon? = null)

val WebSegmentButtonStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .gap(HandpassSpacing.XXS)
            .padding(HandpassSpacing.XS, HandpassSpacing.SM)
            .borderRadius(RadiusButton.value())
            .color(OnSurfaceMuted.value())
            .cursor(Cursor.Pointer)
            .transitionSurfaceAction()
            .minHeight(TouchTarget.value())
            .styleModifier {
                property("flex", "1")
                property("min-width", "0")
                property("white-space", "nowrap")
                property("font-size", "${WebType.Micro}")
                property("border", "none")
                property("font-family", "inherit")
                property("background", Transparent.value().toString())
            }
    }
    hover {
        Modifier
            .backgroundColor(Surface.value())
            .color(OnSurface.value())
    }
    active {
        Modifier.scale(WebMotion.PressScale)
    }
    focusVisible {
        Modifier
            .outline(WebSize.P0.value(), LineStyle.None, Transparent.value())
            .boxShadow(spreadRadius = WebSize.P3.value(), color = AccentRing.value())
    }
}

@Composable
fun <T> WebSegmentedControl(
    label: String,
    segments: List<WebSegment<T>>,
    selected: T,
    onSelect: (T) -> Unit,
) {
    Div({
        attr("role", "group")
        attr("aria-label", label)
        style {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "${HandpassSpacing.XXS}")
            property("background-color", SurfaceMuted.value().toString())
            property("border-radius", "${RadiusButton.value()}")
            property("padding", WebSize.P3.value().toString())
        }
    }) {
        segments.forEach { segment ->
            val isSelected = segment.key == selected
            Button(
                attrs = WebSegmentButtonStyle.toAttrs<AttrsScope<HTMLButtonElement>> {
                    onClick { onSelect(segment.key) }
                    attr("type", "button")
                    attr("aria-label", segment.label)
                    attr("aria-pressed", isSelected.toString())
                    style {
                        if (isSelected) {
                            property("background-color", Surface.value().toString())
                            property("color", Accent.value().toString())
                            property("font-weight", "600")
                            property("box-shadow", Shadow.Card)
                        }
                    }
                }
            ) {
                segment.icon?.let { Icon(it, size = WebIconSize.Xs) }
                Span { Text(segment.label) }
            }
        }
    }
}
